import React, { useEffect, useState } from "react";
import { RiMenu3Line, RiCloseLine } from "react-icons/ri";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "./NavbarComp.css";
import { useQuery } from "@tanstack/react-query";
import Loader from "../../components/Loader/Loader";
import { useLanguage } from "../../Context/LanguageContext";
import fallbackDataNavbarEN from "../../FallbackAPI/NavbarLocal/FallbackApiNavbarEN.json";
import fallbackDataNavbarKR from "../../FallbackAPI/NavbarLocal/FallbackApiNavbarKR.json";

const NavbarComp = () => {
  const [toggleMenu, setToggleMenu] = useState(false);
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const isActive = (path) => {
    if (path === "/careers") {
      return (
        location.pathname.startsWith("/careers") ||
        location.pathname.startsWith("/jobs")
      );
    }

    return location.pathname.startsWith(path);
  };

  const fetchNavbarData = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1500); // 1.5s timeout

    try {
      const response = await fetch(`${baseUrl}/navbar/${language}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        signal: controller.signal,
      });

      clearTimeout(timeout);
      if (!response.ok) {
        throw new Error("Failed to fetch careers data");
      }
      return response.json();
    } catch (error) {
      console.warn("API request failed, using fallback data.");
      return language === "en" ? fallbackDataNavbarEN : fallbackDataNavbarKR;
    }
  };

  const {
    data: apiData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["navbarData", language],
    queryFn: fetchNavbarData,
    staleTime: 10 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setToggleMenu(false); // Close the menu when language is changed
  };

  const getLanguageClass = (lang) =>
    language === lang ? "language-toggler active" : "language-toggler";

  useEffect(() => {
    // Close the menu when the route changes
    setToggleMenu(false);
  }, [location.pathname]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error loading data.</div>;
  }

  if (!apiData?.isEnabled) {
    return null;
  }

  const Menu = () => (
    <>
      {apiData &&
        apiData.pageLinks
          ?.filter((link) => link.isEnabled)
          .sort((a, b) => a.order - b.order)
          .map((link) => (
            <li key={link.id} className="nav-item">
              <NavLink
                to={link.url}
                className={
                  isActive(link.url)
                    ? "nav-link nav-active navlink-ltr"
                    : "nav-link nav-link-ltr"
                }
                onClick={() => handleTabClick(link.url)} // OnClick handler for navigation
              >
                {link.pageName}
              </NavLink>
            </li>
          ))}
    </>
  );
  const handleTabClick = (url) => {
    // Prevent scrolling behavior and just navigate to the top of the page directly
    navigate(url, { replace: true });
    window.scrollTo(0, 0); // This will directly scroll to the top without animation
  };

  return (
    <div className="navbar fixed-top">
      <a className="navbar-logo" href="/">
        {apiData.logoUrl ? (
          <img src={apiData.logoUrl} alt="logo" loading="lazy" />
        ) : (
          <div></div>
        )}
      </a>
      <div className="navbar-links-container d-flex align-items-center">
        <div className="links">
          <Menu />
        </div>
        <div className="language-selector">
          <div className="language">
            <span
              className={getLanguageClass("ko-KR")}
              onClick={() => handleLanguageChange("ko-KR")}
            >
              kor
            </span>
          </div>
          <div className="vertical-line"></div>
          <div className="language">
            <span
              className={getLanguageClass("en")}
              onClick={() => handleLanguageChange("en")}
            >
              eng
            </span>
          </div>
        </div>
      </div>

      <div className="navbar-menu">
        {toggleMenu ? (
          <RiCloseLine
            color="black"
            size={25}
            onClick={() => setToggleMenu(false)}
          />
        ) : (
          <RiMenu3Line
            color="black"
            size={25}
            onClick={() => setToggleMenu(true)}
          />
        )}
        {toggleMenu && (
          <div className="navbar-menu-container scale-up-center">
            <div className="navbar-menu-container-links">
              <Menu />
            </div>
            <div className="language-selector">
              <div className="language">
                <span
                  className={getLanguageClass("ko-KR")}
                  onClick={() => handleLanguageChange("ko-KR")}
                >
                  kor
                </span>
              </div>
              <div className="vertical-line"></div>
              <div className="language">
                <span
                  className={getLanguageClass("en")}
                  onClick={() => handleLanguageChange("en")}
                >
                  eng
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavbarComp;
